import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Check, ArrowRight, Phone, Play } from 'lucide-react'
import { useState, useEffect, useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn, extractErrorMessage } from '@/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { PaymentInterval, ESPType } from '@/types/subscription.types'
import { PlanPrice } from '@/pages/billing/pricing-data'
import { useCreateSenderProfileSubscription } from '@/hooks/use-subscription'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface SeedProvider {
  id: string
  name: string
  icon: string
  description: string
  features: string[]
  monthlyPrice: number
  seedAccountOptions: number[]
  defaultSeedAccounts: number
  senderProfileOptions: number[]
  defaultSenderProfiles: number
}

const seedProviders: SeedProvider[] = [
  {
    id: 'gmail',
    name: 'Gmail Seeds',
    icon: '/providers-logos/gmail.png',
    description: 'For accessing Gmail seed accounts and warmup engagement.',
    features: [
      'Opens emails',
      'Scrolls through messages',
      'Clicks links',
      'Applies labels',
      'Marks as important',
      'Moves emails out of Spam',
      'Whitelists your sender',
      'Marks as Not Spam',
      'Seedlist auto-rotation',
      'API access',
      'Dedicated account manager',
    ],
    monthlyPrice: 500,
    seedAccountOptions: Array.from({ length: 20 }, (_, i) => (i + 1) * 500), // [500, 1000, 1500, ..., 10000]
    defaultSeedAccounts: 500,
    senderProfileOptions: Array.from({ length: 10 }, (_, i) => i + 1), // [1, 2, 3, ..., 10]
    defaultSenderProfiles: 1,
  },
  {
    id: 'outlook',
    name: 'Outlook Seeds',
    icon: '/providers-logos/outlook.png',
    description: 'For accessing Outlook seed accounts and warmup engagement.',
    features: [
      'Opens emails',
      'Scrolls through messages',
      'Clicks links',
      'Applies labels',
      'Marks as important',
      'Moves emails out of Spam',
      'Whitelists your sender',
      'Marks as Not Spam',
      'Seedlist auto-rotation',
      'API access',
      'Dedicated account manager',
    ],
    monthlyPrice: 450,
    seedAccountOptions: [500, 1000, 1500, 2000, 2500],
    defaultSeedAccounts: 500,
    senderProfileOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    defaultSenderProfiles: 1,
  },
  {
    id: 'yahoo',
    name: 'Yahoo Seeds',
    icon: '/providers-logos/yahoo.png',
    description: 'For accessing Yahoo seed accounts and warmup engagement.',
    features: [
      'Opens emails',
      'Scrolls through messages',
      'Clicks links',
      'Applies labels',
      'Marks as important',
      'Moves emails out of Spam',
      'Whitelists your sender',
      'Marks as Not Spam',
      'Seedlist auto-rotation',
      'API access',

      'Dedicated account manager',
    ],
    monthlyPrice: 400,
    seedAccountOptions: [500, 1000, 1500, 2000, 2500],
    defaultSeedAccounts: 500,
    senderProfileOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    defaultSenderProfiles: 1,
  },
  {
    id: 'google-workspace',
    name: 'Google Workspace',
    icon: '/providers-logos/google-workspace.png',
    description: 'For accessing Google Workspace seed accounts and warmup engagement.',
    features: [
      'Opens emails',
      'Scrolls through messages',
      'Clicks links',
      'Applies labels',
      'Marks as important',
      'Moves emails out of Spam',
      'Whitelists your sender',
      'Marks as Not Spam',
      'Seedlist auto-rotation',
      'API access',

      'Dedicated account manager',
    ],
    monthlyPrice: 550,
    seedAccountOptions: [500, 1000, 1500, 2000, 2500],
    defaultSeedAccounts: 500,
    senderProfileOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    defaultSenderProfiles: 1,
  },
  {
    id: 'microsoft-365',
    name: 'Microsoft 365',
    icon: '/providers-logos/microsoft-365.png',
    description: 'For accessing Microsoft 365 seed accounts and warmup engagement.',
    features: [
      'Opens emails',
      'Scrolls through messages',
      'Clicks links',
      'Applies labels',
      'Marks as important',
      'Moves emails out of Spam',
      'Whitelists your sender',
      'Marks as Not Spam',
      'Seedlist auto-rotation',
      'API access',

      'Dedicated account manager',
    ],
    monthlyPrice: 550,
    seedAccountOptions: [500, 1000, 1500, 2000, 2500],
    defaultSeedAccounts: 500,
    senderProfileOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    defaultSenderProfiles: 1,
  },
  {
    id: 'aol',
    name: 'AOL Mail',
    icon: '/providers-logos/aol.png',
    description: 'For accessing AOL Mail seed accounts and warmup engagement.',
    features: [
      'Opens emails',
      'Scrolls through messages',
      'Clicks links',
      'Applies labels',
      'Marks as important',
      'Moves emails out of Spam',
      'Whitelists your sender',
      'Marks as Not Spam',
      'Seedlist auto-rotation',
      'API access',

      'Dedicated account manager',
    ],
    monthlyPrice: 350,
    seedAccountOptions: [500, 1000, 1500, 2000, 2500],
    defaultSeedAccounts: 500,
    senderProfileOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    defaultSenderProfiles: 1,
  },
  {
    id: 'icloud',
    name: 'iCloud Mail',
    icon: '/providers-logos/icloud.png',
    description: 'For accessing iCloud Mail seed accounts and warmup engagement.',
    features: [
      'Opens emails',
      'Scrolls through messages',
      'Clicks links',
      'Applies labels',
      'Marks as important',
      'Moves emails out of Spam',
      'Whitelists your sender',
      'Marks as Not Spam',
      'Seedlist auto-rotation',
      'API access',

      'Dedicated account manager',
    ],
    monthlyPrice: 450,
    seedAccountOptions: [500, 1000, 1500, 2000, 2500],
    defaultSeedAccounts: 500,
    senderProfileOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    defaultSenderProfiles: 1,
  },
]

interface SeedListContentProps {
  interval?: keyof PlanPrice
}

export function SeedListContent({ interval }: SeedListContentProps = {}) {
  const navigate = useNavigate()
  
  // Default to monthly if interval is not provided
  const defaultInterval = interval || PaymentInterval.MONTHLY
  
  // Convert interval to billing period format for backward compatibility
  const billingPeriod = defaultInterval === PaymentInterval.ANNUAL 
    ? 'annually' 
    : defaultInterval === PaymentInterval.SEMI_YEARLY
    ? 'semi-yearly'
    : defaultInterval === PaymentInterval.QUARTERLY
    ? 'quarterly'
    : 'monthly'
  
  // State for each provider's configuration
  const [providerConfigs, setProviderConfigs] = useState<Record<string, {
    seedAccounts: number | 'custom'
    senderProfiles: number | 'custom'
    customSeedAccounts?: number
    customSenderProfiles?: number
  }>>(() => {
    const configs: Record<string, { seedAccounts: number | 'custom'; senderProfiles: number | 'custom'; customSeedAccounts?: number; customSenderProfiles?: number }> = {}
    seedProviders.forEach(provider => {
      configs[provider.id] = {
        seedAccounts: provider.defaultSeedAccounts,
        senderProfiles: provider.defaultSenderProfiles,
      }
    })
    return configs
  })

  // Ensure state is properly initialized on mount
  useEffect(() => {
    // Verify and reset to defaults if any config is missing or invalid
    setProviderConfigs(prev => {
      const updated = { ...prev }
      let hasChanges = false
      
      seedProviders.forEach(provider => {
        if (!updated[provider.id] || 
            typeof updated[provider.id].seedAccounts !== 'number' || 
            typeof updated[provider.id].senderProfiles !== 'number') {
          updated[provider.id] = {
            seedAccounts: provider.defaultSeedAccounts,
            senderProfiles: provider.defaultSenderProfiles,
          }
          hasChanges = true
        }
      })
      
      return hasChanges ? updated : prev
    })
  }, [])

  const updateProviderConfig = (
    providerId: string,
    field: 'seedAccounts' | 'senderProfiles' | 'customSeedAccounts' | 'customSenderProfiles',
    value: number | 'custom'
  ) => {
    setProviderConfigs(prev => ({
      ...prev,
      [providerId]: {
        ...prev[providerId],
        [field]: value,
      },
    }))
  }
  
  // Calculate emails per day and per month based on configuration
  const calculateEmails = (config: { seedAccounts: number | 'custom'; senderProfiles: number | 'custom'; customSeedAccounts?: number; customSenderProfiles?: number }, provider: SeedProvider) => {
    const seedAccountsValue = typeof config.seedAccounts === 'number' 
      ? config.seedAccounts 
      : (config.customSeedAccounts || provider.defaultSeedAccounts)
    const senderProfilesValue = typeof config.senderProfiles === 'number' 
      ? config.senderProfiles 
      : (config.customSenderProfiles || provider.defaultSenderProfiles)
    
    // Emails per day = Seed Accounts × Sender Profiles
    const emailsPerDay = seedAccountsValue * senderProfilesValue
    // Emails per month = Emails per day × 30
    const emailsPerMonth = emailsPerDay * 30
    
    return { emailsPerDay, emailsPerMonth }
  }

  const calculatePrice = (provider: SeedProvider, configOverride?: { seedAccounts: number | 'custom'; senderProfiles: number | 'custom'; customSeedAccounts?: number; customSenderProfiles?: number }) => {
    // Use provided config or fallback to state, then to defaults
    const config = configOverride || providerConfigs[provider.id] || {
      seedAccounts: provider.defaultSeedAccounts,
      senderProfiles: provider.defaultSenderProfiles,
    }
    
    // Pricing model: $0.30 per seed, first sender profile included, additional profiles cost 60% of base price each
    const SEED_PRICE_PER_UNIT = 0.30
    const SENDER_PROFILE_MULTIPLIER = 0.60 // 60% of base price for additional profiles
    
    const seedAccountsValue = typeof config.seedAccounts === 'number' 
      ? config.seedAccounts 
      : (config.customSeedAccounts || provider.defaultSeedAccounts)
    const senderProfilesValue = typeof config.senderProfiles === 'number' 
      ? config.senderProfiles 
      : (config.customSenderProfiles || provider.defaultSenderProfiles)
    
    // Ensure we have valid numbers - enforce defaults if invalid
    const validSeedAccounts = (Number.isFinite(seedAccountsValue) && seedAccountsValue > 0) 
      ? seedAccountsValue 
      : provider.defaultSeedAccounts
    const validSenderProfiles = (Number.isFinite(senderProfilesValue) && senderProfilesValue > 0) 
      ? senderProfilesValue 
      : provider.defaultSenderProfiles
    
    // Calculate base price: seed accounts * $0.30 (includes first sender profile)
    const basePrice = validSeedAccounts * SEED_PRICE_PER_UNIT
    
    // Calculate additional profiles (sender profiles - 1, since first is included in base)
    const additionalProfiles = Math.max(0, validSenderProfiles - 1)
    
    // Calculate sender profile cost: 60% of base price per additional profile
    const senderProfileCost = basePrice * SENDER_PROFILE_MULTIPLIER * additionalProfiles
    
    // Final monthly price = base price + sender profile costs
    const finalPrice = basePrice + senderProfileCost
    
    const currentInterval = defaultInterval || PaymentInterval.MONTHLY
    
    // Calculate original price (before discount)
    let originalPrice: number
    if (currentInterval === PaymentInterval.ANNUAL) {
      originalPrice = finalPrice * 12
    } else if (currentInterval === PaymentInterval.SEMI_YEARLY) {
      originalPrice = finalPrice * 6
    } else if (currentInterval === PaymentInterval.QUARTERLY) {
      originalPrice = finalPrice * 3
    } else {
      originalPrice = finalPrice
    }
    
    // Calculate discounted price
    let discountedPrice: number
    if (currentInterval === PaymentInterval.ANNUAL) {
      // 20% off for annual (80% of price)
      discountedPrice = originalPrice * 0.8
    } else if (currentInterval === PaymentInterval.SEMI_YEARLY) {
      // 15% off for semi-yearly (85% of price)
      discountedPrice = originalPrice * 0.85
    } else if (currentInterval === PaymentInterval.QUARTERLY) {
      // 10% off for quarterly (90% of price)
      discountedPrice = originalPrice * 0.9
    } else {
      // No discount for monthly
      discountedPrice = originalPrice
    }
    
    return { originalPrice, discountedPrice }
  }

  // Map provider ID to ESP type
  const getESPType = (providerId: string): ESPType => {
    const mapping: Record<string, ESPType> = {
      'gmail': 'Gmail',
      'outlook': 'Outlook',
      'yahoo': 'Yahoo',
      'google-workspace': 'Google Workspace',
      'microsoft-365': 'Microsoft 365',
      'aol': 'AOL',
      'icloud': 'iCloud',
    }
    return mapping[providerId] || 'Gmail'
  }

  const { mutateAsync: createSubscription, isPending: isCreatingSubscription } = useCreateSenderProfileSubscription()
  
  // State for confirmation dialog
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [pendingProviderId, setPendingProviderId] = useState<string | null>(null)

  const handleAddSeeds = (providerId: string) => {
    const config = providerConfigs[providerId]
    
    // Skip if custom values are selected - navigate directly without confirmation
    if (config.seedAccounts === 'custom' || config.senderProfiles === 'custom') {
      navigate({
        to: '/seed-list/$seed-list',
        params: { 'seed-list': providerId }
      })
      return
    }

    // Show confirmation dialog for standard purchases
    setPendingProviderId(providerId)
    setShowConfirmation(true)
  }

  const handleConfirmPurchase = async () => {
    if (!pendingProviderId) return

    const config = providerConfigs[pendingProviderId]
    
    const seedAccountsValue = typeof config.seedAccounts === 'number' 
      ? config.seedAccounts 
      : (config.customSeedAccounts || seedProviders.find(p => p.id === pendingProviderId)?.defaultSeedAccounts || 500)
    
    const senderProfilesValue = typeof config.senderProfiles === 'number' 
      ? config.senderProfiles 
      : (config.customSenderProfiles || seedProviders.find(p => p.id === pendingProviderId)?.defaultSenderProfiles || 1)

    const espType = getESPType(pendingProviderId)

    try {
      setShowConfirmation(false)
      await createSubscription({
        interval: defaultInterval as PaymentInterval,
        lineItems: [
          {
            senderCount: senderProfilesValue,
            seedCountPerSender: seedAccountsValue,
            espTypes: [espType],
          },
        ],
      })
      toast.success('Subscription created successfully! Redirecting to checkout...')
      // The hook will handle redirecting to checkout URL if provided
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to create subscription')
      toast.error(message)
    } finally {
      setPendingProviderId(null)
    }
  }

  return (
    <div className="w-full space-y-12">
      {/* Seed Provider Cards - Stacked vertically */}
      <div className="space-y-8 pt-4 pb-4">
        {seedProviders.map((provider) => {
          // AOL Mail and iCloud Mail are coming soon
          const isComingSoon = provider.id === 'aol' || provider.id === 'icloud'
          
          if (isComingSoon) {
            return (
              <ComingSoonCard key={provider.id} provider={provider} />
            )
          }
          
          const config = providerConfigs[provider.id] || {
            seedAccounts: provider.defaultSeedAccounts,
            senderProfiles: provider.defaultSenderProfiles,
          }
          // Pass config to ensure calculation uses correct values
          const priceData = calculatePrice(provider, config)
          
          return (
            <SeedProviderCard
              key={provider.id}
              provider={provider}
              config={config}
              billingPeriod={billingPeriod}
              originalPrice={priceData.originalPrice}
              discountedPrice={priceData.discountedPrice}
              interval={defaultInterval}
              onConfigChange={(field, value) => updateProviderConfig(provider.id, field, value)}
              onAddSeeds={() => handleAddSeeds(provider.id)}
              calculateEmails={calculateEmails}
              isCreatingSubscription={isCreatingSubscription}
            />
          )
        })}
      </div>

      {/* What is a Seed List Section */}
      <div className="w-full pt-4 pb-4 pr-0">
        <div className="space-y-6 w-full">
          <h2 className="text-3xl font-bold text-foreground text-left">What is a Seed List?</h2>
          <p className="text-base text-muted-foreground w-full text-left">
            A seed list is a carefully curated collection of email addresses that monitor your email campaigns and provide feedback on their deliverability, content, and compliance. These are typically placed by mailbox providers and email service providers to help senders understand how their emails are being received and displayed across different email clients and folders.
          </p>
          
          {/* Feature Boxes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <Card className="bg-blue-50/50 dark:bg-blue-950/10 border-blue-100 dark:border-blue-900/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-bold text-blue-600 dark:text-blue-400 mb-2">
                      Deliverability Monitoring
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Track where your emails land (inbox, spam, promotions) across major email providers.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-blue-50/50 dark:bg-blue-950/10 border-blue-100 dark:border-blue-900/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-bold text-blue-600 dark:text-blue-400 mb-2">
                      Warm Up Your IP
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Gradually build sender reputation with consistent, low-volume sending to seed addresses.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-blue-50/50 dark:bg-blue-950/10 border-blue-100 dark:border-blue-900/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-bold text-blue-600 dark:text-blue-400 mb-2">
                      Content Validation
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Identify rendering issues, spam triggers, and authentication problems before sending at scale.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-blue-50/50 dark:bg-blue-950/10 border-blue-100 dark:border-blue-900/30">
              <CardContent className="p-6">
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-bold text-blue-600 dark:text-blue-400 mb-2">
                      Data Insights
                    </h3>
                    <p className="text-sm text-muted-foreground">
                    Get detailed analytics on received rate, open rates, click rates, move out of spam etc. across all the seed accounts.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Learn More Section */}
      <div className="w-full pt-4 pb-4">
        <div className="space-y-6">
          <h2 className="text-3xl font-bold text-foreground text-left">GUI Based Warmups VS Imap Based Warmups</h2>
          <p className="text-base text-muted-foreground text-left whitespace-nowrap">
          Watch these videos to understand the difference between the Human Powered Email Warmups and Imap Based warmups!
          </p>
          
          {/* Video Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
            <VideoCard
              videoId="Qdx2PRXhEQs"
              title="Human Powered Warmups"
              description="Check out the video to see how InboxDoctor does email warmups which build real reputation."
              thumbnail="/video-thumbnails/human-powered-warmups.jpg"
            />
            <VideoCard
              videoId="Kd2b2SHxRnQ"
              title="Imap Based Email Warmups"
              description="Check out the video to understand how fake reputation is build using the imap based warmups."
              thumbnail="/video-thumbnails/imap-based-warmups.jpg"
            />
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog 
        open={showConfirmation} 
        onOpenChange={(open) => {
          setShowConfirmation(open)
          if (!open) {
            setPendingProviderId(null)
          }
        }}
      >
        <AlertDialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Your Purchase</AlertDialogTitle>
            <AlertDialogDescription>
              This action will complete your order and charge your default payment method. Do you wish to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              variant="default" 
              onClick={handleConfirmPurchase}
              disabled={isCreatingSubscription}
            >
              {isCreatingSubscription ? 'Processing...' : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface ComingSoonCardProps {
  provider: SeedProvider
}

function ComingSoonCard({ provider }: ComingSoonCardProps) {
  return (
    <Card className="w-full opacity-75">
      <CardContent className="p-6">
        {/* Header with Title and Description */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <img
              src={provider.icon}
              alt={`${provider.name} icon`}
              className="w-6 h-6 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <h3 className="text-lg font-bold">{provider.name}</h3>
          </div>
          
          <p className="text-sm text-muted-foreground md:max-w-md">
            {provider.description}
          </p>
        </div>

        {/* Coming Soon Message */}
        <div className="flex flex-col items-center justify-center py-12 border-t border-dashed">
          <div className="text-center">
            <p className="text-lg font-semibold text-muted-foreground">Coming Soon</p>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

interface SeedProviderCardProps {
  provider: SeedProvider
  config: { seedAccounts: number | 'custom'; senderProfiles: number | 'custom'; customSeedAccounts?: number; customSenderProfiles?: number }
  billingPeriod: 'monthly' | 'quarterly' | 'semi-yearly' | 'annually'
  originalPrice: number
  discountedPrice: number
  interval?: keyof PlanPrice
  onConfigChange: (field: 'seedAccounts' | 'senderProfiles' | 'customSeedAccounts' | 'customSenderProfiles', value: number | 'custom') => void
  onAddSeeds: () => void
  calculateEmails: (config: { seedAccounts: number | 'custom'; senderProfiles: number | 'custom'; customSeedAccounts?: number; customSenderProfiles?: number }, provider: SeedProvider) => { emailsPerDay: number; emailsPerMonth: number }
  isCreatingSubscription?: boolean
}

function SeedProviderCard({
  provider,
  config,
  billingPeriod,
  originalPrice,
  discountedPrice,
  interval,
  onConfigChange,
  onAddSeeds,
  calculateEmails,
  isCreatingSubscription = false,
}: SeedProviderCardProps) {
  const { emailsPerDay, emailsPerMonth } = calculateEmails(config, provider)
  return (
    <Card className="w-full">
      <CardContent className="pt-6 px-6 pb-6">
        {/* Header with Title and Description */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <img
              src={provider.icon}
              alt={`${provider.name} icon`}
              className="w-6 h-6 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none'
              }}
            />
            <h3 className="text-lg font-bold">{provider.name}</h3>
          </div>
          
          {/* Description and Features side by side */}
          <div className="flex flex-col md:flex-row md:items-start gap-8">
            <p className="text-sm text-muted-foreground md:max-w-md m-0">
              {provider.description}
            </p>
            
            {/* Features on the right in 3 columns (4 rows) - responsive: 1 col mobile, 2 col tablet, 3 col desktop */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 md:flex-shrink-0 md:ml-auto w-full md:w-auto">
              {provider.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm text-muted-foreground">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Configuration Inputs - Horizontal layout with fixed narrow widths */}
        <div className="flex flex-col md:flex-row md:items-start gap-8 pt-6 border-t mb-8">
          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium">Seed Accounts</label>
            <div className="flex flex-col space-y-2">
              <Select
                value={config.seedAccounts === 'custom' ? 'custom' : (provider.seedAccountOptions.includes(config.seedAccounts as number) ? config.seedAccounts.toString() : 'custom')}
                onValueChange={(value) => {
                  if (value === 'custom') {
                    onConfigChange('seedAccounts', 'custom')
                  } else {
                    onConfigChange('seedAccounts', parseInt(value))
                  }
                }}
              >
                <SelectTrigger className="w-full min-w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {provider.seedAccountOptions.map((option) => (
                    <SelectItem key={option} value={option.toString()}>
                      {option.toLocaleString()}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col space-y-2">
            <label className="text-sm font-medium">Sender Profiles</label>
            <div className="flex flex-col space-y-2">
              <Select
                value={config.senderProfiles === 'custom' ? 'custom' : (provider.senderProfileOptions.includes(config.senderProfiles as number) ? config.senderProfiles.toString() : 'custom')}
                onValueChange={(value) => {
                  if (value === 'custom') {
                    onConfigChange('senderProfiles', 'custom')
                  } else {
                    onConfigChange('senderProfiles', parseInt(value))
                  }
                }}
              >
                <SelectTrigger className="w-full min-w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {provider.senderProfileOptions.map((option) => (
                    <SelectItem key={option} value={option.toString()}>
                      {option}
                    </SelectItem>
                  ))}
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Warmup Emails Per Day and Per Month Display */}
          <div className="flex flex-row gap-6 md:ml-auto">
            <div className="flex flex-col space-y-1 bg-blue-50 dark:bg-blue-950/20 rounded-lg px-3 py-2 border border-blue-100 dark:border-blue-900/50 min-w-[160px]">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight">Warmup Emails Per Day</label>
              <div className="text-lg font-bold text-blue-600 dark:text-blue-500 leading-tight">{emailsPerDay.toLocaleString()}</div>
            </div>
            <div className="flex flex-col space-y-1 bg-blue-50 dark:bg-blue-950/20 rounded-lg px-3 py-2 border border-blue-100 dark:border-blue-900/50 min-w-[160px]">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight">Warmup Emails Per Month</label>
              <div className="text-lg font-bold text-blue-600 dark:text-blue-500 leading-tight">{emailsPerMonth.toLocaleString()}</div>
            </div>
          </div>
        </div>

          {/* Pricing and Action - Bottom section */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 pt-4 border-t">
          <div className="flex flex-col gap-0">
            {config.seedAccounts === 'custom' || config.senderProfiles === 'custom' ? (
              <p className="text-base font-semibold text-muted-foreground">Contact Sales</p>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <p className="text-xl font-semibold">
                    {interval === PaymentInterval.ANNUAL 
                      ? `$${(discountedPrice / 12).toFixed(0)}`
                      : interval === PaymentInterval.SEMI_YEARLY
                      ? `$${(discountedPrice / 6).toFixed(0)}`
                      : interval === PaymentInterval.QUARTERLY
                      ? `$${(discountedPrice / 3).toFixed(0)}`
                      : `$${discountedPrice.toFixed(0)}`
                    }
                  </p>
                  {discountedPrice < originalPrice && (
                    <>
                      <span className="text-base text-muted-foreground line-through">
                        {interval === PaymentInterval.ANNUAL 
                          ? `$${(originalPrice / 12).toFixed(0)}`
                          : interval === PaymentInterval.SEMI_YEARLY
                          ? `$${(originalPrice / 6).toFixed(0)}`
                          : interval === PaymentInterval.QUARTERLY
                          ? `$${(originalPrice / 3).toFixed(0)}`
                          : `$${originalPrice.toFixed(0)}`
                        }
                      </span>
                    </>
                  )}
                  <span className="text-base text-muted-foreground">per month</span>
                </div>
                {interval === PaymentInterval.ANNUAL && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-0">Billed annually</p>
                )}
                {interval === PaymentInterval.SEMI_YEARLY && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-0">Billed semi-yearly</p>
                )}
                {interval === PaymentInterval.QUARTERLY && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-0">Billed quarterly</p>
                )}
                {interval === PaymentInterval.MONTHLY && (
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-0">Billed monthly</p>
                )}
              </>
            )}
          </div>
          {config.seedAccounts === 'custom' || config.senderProfiles === 'custom' ? (
            <Button
              onClick={() => {
                window.open('https://calendly.com/inboxdoctor-sales/30min?month=2025-11', '_blank')
              }}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 px-6 py-2.5 rounded-lg flex items-center gap-2 group"
            >
              <Phone className="w-4 h-4" />
              Book a Call
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
            </Button>
          ) : (
            <Button
              onClick={onAddSeeds}
              disabled={isCreatingSubscription}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg hover:shadow-xl transition-all duration-200 px-6 py-2.5 rounded-lg flex items-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreatingSubscription ? 'Processing...' : 'Buy Now'}
              {!isCreatingSubscription && (
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

interface VideoCardProps {
  videoId: string
  title: string
  description: string
  thumbnail?: string
}

function VideoCard({ videoId, title, description, thumbnail }: VideoCardProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  const thumbnailUrl = thumbnail || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
  const fallbackThumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1`

  const handlePlay = () => {
    setIsPlaying(true)
  }

  const handleClose = () => {
    setIsPlaying(false)
  }

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    if (!imageError) {
      setImageError(true)
      e.currentTarget.src = fallbackThumbnailUrl
    }
  }

  return (
    <>
      <Card className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow border border-border" onClick={handlePlay}>
        <CardContent className="p-0">
          {/* Video Thumbnail */}
          <div className="relative w-full aspect-video bg-gray-200 dark:bg-gray-800 rounded-t-lg overflow-hidden">
            {!imageLoaded && !imageError && (
              <div className="absolute inset-0 bg-gray-200 dark:bg-gray-800 animate-pulse flex items-center justify-center">
                <div className="text-gray-400 text-sm">Loading thumbnail...</div>
              </div>
            )}
            <img
              src={imageError ? fallbackThumbnailUrl : thumbnailUrl}
              alt={title}
              className={`w-full h-full object-cover ${imageLoaded ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
              onLoad={() => setImageLoaded(true)}
              onError={handleImageError}
              loading="eager"
              style={{ minHeight: '100%', minWidth: '100%' }}
            />
            {/* Play Button Overlay - Larger and more prominent */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/20 transition-colors pointer-events-none">
              <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors shadow-xl hover:scale-110 transform transition-transform pointer-events-auto">
                <Play className="w-12 h-12 text-white ml-1" fill="white" strokeWidth={2} />
              </div>
            </div>
          </div>
          
          {/* Video Info */}
          <div className="p-6">
            <h3 className="text-xl font-bold text-foreground mb-2">{title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
          </div>
        </CardContent>
      </Card>

      {/* Modal for video playback */}
      {isPlaying && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={handleClose}
        >
          <div
            className="relative w-full max-w-4xl aspect-video bg-black rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-10 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors text-2xl leading-none"
              aria-label="Close video"
            >
              ×
            </button>
            <iframe
              src={embedUrl}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={title}
            />
          </div>
        </div>
      )}
    </>
  )
}

